import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import clsx from 'clsx';
import { Badge, Box, Fab, makeStyles, Theme, createStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { FiChevronDown } from 'react-icons/fi';

import { notificationBadge } from '@vyce/core/src/theme/styles';

import ChatDateSeparator from '../ChatDateSeparator';
import { ThreadMessageMeta, threadItems } from './threadItems';

// Within this distance of the bottom the thread follows new messages.
const STICK_PX = 80;
// Within this distance of the top the thread asks for older messages.
const OLDER_PX = 240;
const HIGHLIGHT_MS = 1600;

const scrollToEnd = (el: HTMLDivElement | null, behavior: ScrollBehavior) =>
  el?.scrollTo?.({ top: el.scrollHeight, behavior });

export interface ChatThreadRef {
  scrollToMessage: (id: string) => void;
  scrollToBottom: () => void;
}

export interface ChatThreadProps<T> {
  // Oldest first.
  messages: T[];
  meta: (message: T) => ThreadMessageMeta;
  renderMessage: (message: T, position: { first: boolean; last: boolean }) => React.ReactNode;
  // The user's own new message always brings the thread back down; others
  // that arrive while they read further up are counted on the jump button.
  isOwn?: (message: T) => boolean;
  // Shown under the last message, e.g. "typing…".
  typing?: React.ReactNode;
  // Shown instead of the thread while there are no messages.
  emptyState?: React.ReactNode;
  // Asked for when the user scrolls near the top.
  onLoadOlder?: () => void;
  hasOlder?: boolean;
  className?: string;
}

// The conversation wall shared by every chat: Telegram-style date chips and
// bursts, follows new messages unless the user scrolled up to read, a jump
// button with the count of unseen ones, and older messages loaded on scroll
// without the view jumping.
const ChatThreadInner = <T,>(
  {
    messages,
    meta,
    renderMessage,
    isOwn,
    typing,
    emptyState,
    onLoadOlder,
    hasOlder = true,
    className,
  }: ChatThreadProps<T>,
  ref: React.Ref<ChatThreadRef>
) => {
  const classes = useStyles();
  const scroller = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const items = useMemo(() => threadItems(messages, meta), [messages, meta]);
  const firstId = messages.length ? meta(messages[0]).id : undefined;
  const lastId = messages.length ? meta(messages[messages.length - 1]).id : undefined;
  const seen = useRef({ firstId, lastId, count: messages.length, height: 0 });
  const askedOlderFor = useRef<string | undefined>(undefined);

  const scrollToBottom = useCallback(() => scrollToEnd(scroller.current, 'smooth'), []);

  const scrollToMessage = useCallback(
    (id: string) => {
      const element = Array.from(
        scroller.current?.querySelectorAll<HTMLElement>('[data-message-id]') ?? []
      ).find(item => item.dataset.messageId === id);
      if (!element) {
        return;
      }
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.remove(classes.highlight);
      // Restarts the flash when the same message is jumped to twice.
      void element.offsetWidth;
      element.classList.add(classes.highlight);
      setTimeout(() => element.classList.remove(classes.highlight), HIGHLIGHT_MS);
    },
    [classes.highlight]
  );

  useImperativeHandle(ref, () => ({ scrollToMessage, scrollToBottom }), [scrollToMessage, scrollToBottom]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) {
      return;
    }
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_PX;
    setShowJump(!atBottom.current);
    if (atBottom.current) {
      setUnseen(0);
    }
    // Once per oldest message, so a slow page isn't asked for twice.
    if (onLoadOlder && hasOlder && el.scrollTop < OLDER_PX && firstId && askedOlderFor.current !== firstId) {
      askedOlderFor.current = firstId;
      onLoadOlder();
    }
  };

  // Follow the conversation unless the user scrolled up to read; their own
  // message always brings them back down. Older messages added on top keep
  // what the user is looking at in place.
  useLayoutEffect(() => {
    const el = scroller.current;
    const before = seen.current;
    const prepended = !!before.firstId && firstId !== before.firstId && lastId === before.lastId;
    const appended = lastId !== before.lastId && !!before.lastId;
    seen.current = { firstId, lastId, count: messages.length, height: el?.scrollHeight ?? 0 };
    if (!el) {
      return;
    }
    if (prepended) {
      el.scrollTop += el.scrollHeight - before.height;
      return;
    }
    if (!before.lastId) {
      // The first page of a conversation opens at its end.
      scrollToEnd(el, 'auto');
      return;
    }
    let added: T[] = [];
    if (appended) {
      const from = messages.findIndex(message => meta(message).id === before.lastId);
      added = from >= 0 ? messages.slice(from + 1) : messages.slice(-1);
    }
    const ownMessage = !!isOwn && added.some(isOwn);
    if (atBottom.current || ownMessage) {
      scrollToEnd(el, added.length ? 'smooth' : 'auto');
    } else if (added.length) {
      setUnseen(value => value + added.length);
    }
  }, [messages, typing]);

  // A different conversation starts at its end with nothing unseen.
  useEffect(() => {
    if (!messages.length) {
      seen.current = { firstId: undefined, lastId: undefined, count: 0, height: 0 };
      atBottom.current = true;
      setUnseen(0);
      setShowJump(false);
    }
  }, [messages.length]);

  if (!messages.length && !typing && emptyState) {
    return <Box className={clsx(classes.wall, classes.empty, className)}>{emptyState}</Box>;
  }

  return (
    <Box className={clsx(classes.wall, className)}>
      <div ref={scroller} onScroll={onScroll} className={classes.scroller}>
        <Box className={classes.column} role="log" aria-live="polite">
          {items.map(item =>
            item.type === 'date' ? (
              <Box key={item.key} className={classes.date}>
                <ChatDateSeparator date={Math.floor(item.date / 1000)} />
              </Box>
            ) : (
              <div key={item.key} data-message-id={item.key} className={classes.item}>
                {renderMessage(item.message, { first: item.first, last: item.last })}
              </div>
            )
          )}
          {typing}
        </Box>
      </div>

      {showJump && (
        <Fab
          size="small"
          className={classes.jump}
          onClick={scrollToBottom}
          aria-label="Scroll to the latest message"
        >
          <Badge badgeContent={unseen} overlap="circular" classes={{ badge: classes.jumpBadge }}>
            <FiChevronDown size={22} />
          </Badge>
        </Fab>
      )}
    </Box>
  );
};

export const ChatThread = forwardRef(ChatThreadInner) as <T>(
  props: ChatThreadProps<T> & { ref?: React.Ref<ChatThreadRef> }
) => ReturnType<typeof ChatThreadInner>;

const useStyles = makeStyles((theme: Theme) => {
  const dot = alpha(theme.palette.primary.main, theme.palette.type === 'dark' ? 0.12 : 0.09);

  return createStyles({
    wall: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      backgroundColor: theme.palette.background.default,
      backgroundImage: `radial-gradient(${dot} 1.3px, transparent 1.6px), radial-gradient(${dot} 1.3px, transparent 1.6px)`,
      backgroundSize: '22px 22px',
      backgroundPosition: '0 0, 11px 11px',
    },
    empty: { overflowY: 'auto', justifyContent: 'center' },
    scroller: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 760,
      margin: '0 auto',
      padding: '12px 16px 16px',
      [theme.breakpoints.down('xs')]: { padding: '8px 12px 12px' },
    },
    item: { display: 'flex', flexDirection: 'column', borderRadius: 12 },
    highlight: {
      animation: `$flash ${HIGHLIGHT_MS}ms ease-out`,
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    date: {
      display: 'flex',
      justifyContent: 'center',
      position: 'sticky',
      top: 4,
      zIndex: 1,
      margin: '8px 0 2px',
    },
    jump: {
      position: 'absolute',
      right: 16,
      bottom: 12,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.secondary,
      '&:hover': { backgroundColor: theme.palette.background.paper },
    },
    jumpBadge: {
      backgroundColor: notificationBadge.background,
      color: notificationBadge.text,
    },
    '@keyframes flash': {
      from: { backgroundColor: alpha(theme.palette.primary.main, 0.18) },
      to: { backgroundColor: alpha(theme.palette.primary.main, 0) },
    },
  });
});
